import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';

import { TgPage, Platform, InstPage, PLATFORM_URL_SEGMENT } from '../types';

import { axios } from '@/libraries/axios';
import { QueryConfig } from '@/libraries/react-query';
import { Departments } from '@/types';

type PlatformPages = {
  telegram: TgPage;
  instagram: InstPage;
};

export const getPages = <P extends Platform>(
  platform: P,
  params: { department?: Departments; isArchived?: boolean }
): Promise<{ pages: PlatformPages[P][] }> => {
  return axios.get(`/${PLATFORM_URL_SEGMENT[platform]}/pages`, { params });
};

type UseGetPages<P extends Platform> = {
  platform: P;
  isArchived?: boolean;
  department?: Departments;
  config?: QueryConfig<() => Promise<{ pages: PlatformPages[P][] }>>;
};

const RESTORE_POLLING_INTERVAL = 5000;

export const useGetPages = <P extends Platform>({
  config = {},
  platform,
  department,
  isArchived,
}: UseGetPages<P>) => {
  const queryClient = useQueryClient();
  const queryKey = useMemo(
    () => ['pages', platform, department, isArchived],
    [platform, department, isArchived]
  );

  const query = useQuery<{ pages: PlatformPages[P][] }>({
    ...config,
    queryKey,
    queryFn: () => getPages(platform, { department, isArchived }),
  });

  const hasRestoringPages = useMemo(() => {
    const pages = query.data?.pages || [];
    return pages.some((page) => page.config.isRestoring === true);
  }, [query.data?.pages]);

  useEffect(() => {
    if (hasRestoringPages && !config.refetchInterval) {
      const interval = setInterval(() => {
        queryClient.refetchQueries({ queryKey });
      }, RESTORE_POLLING_INTERVAL);

      return () => clearInterval(interval);
    }
  }, [hasRestoringPages, queryClient, queryKey, config.refetchInterval]);

  return query;
};
